/**
 * BillingModalButton - Styled button sub-component for BillingModal
 *
 * Four variants with distinct visual hierarchy:
 * - Primary: purple filled (recommended action)
 * - Secondary: gray filled (alternative positive action)
 * - Destructive: transparent with red border (irreversible action, low-prominence)
 * - Text: transparent link-style (dismiss/cancel)
 *
 * All buttons: 48px min-height (grows for subtitle), 12px border radius, full width.
 * Task: #1498
 */
import React, { useState } from 'react';
import { Pressable, Text, ActivityIndicator, View, StyleSheet } from 'react-native';
import type { BillingModalButton as ButtonConfig } from './types';

const COLORS = {
  primary: { bg: '#7C3AED', bgPressed: '#6D28D9', text: '#FFFFFF' },
  secondary: { bg: '#F3F4F6', bgPressed: '#E5E7EB', text: '#374151' },
  destructive: {
    bg: 'transparent',
    bgPressed: '#FEF2F2',
    text: '#DC2626',
    border: '#FCA5A5',
  },
  text: { bg: 'transparent', bgPressed: 'transparent', text: '#6B7280' },
} as const;

interface Props {
  config: ButtonConfig;
  index: number;
  testIDPrefix?: string;
}

export function BillingModalButtonComponent({ config, index, testIDPrefix }: Props) {
  const [pressed, setPressed] = useState(false);
  const { variant, text, subtitle, loading, disabled, onPress, testID } = config;
  const colors = COLORS[variant];

  const isDisabled = disabled || loading;
  const backgroundColor = pressed && !isDisabled ? colors.bgPressed : colors.bg;
  const resolvedTestID = testID || (testIDPrefix ? `${testIDPrefix}-btn-${index}` : undefined);

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      disabled={isDisabled}
      style={[
        styles.button,
        { backgroundColor },
        variant === 'destructive' && styles.destructiveBorder,
        variant === 'text' && styles.textButton,
        isDisabled && styles.disabled,
        pressed && variant === 'text' && styles.textPressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={text}
      accessibilityHint={variant === 'destructive' ? 'This action cannot be undone' : undefined}
      testID={resolvedTestID}
    >
      {loading ? (
        <ActivityIndicator size="small" color={colors.text} />
      ) : (
        <View style={styles.content}>
          <Text
            style={[
              styles.label,
              { color: colors.text },
              variant === 'primary' && styles.labelBold,
              variant === 'destructive' && styles.labelBold,
              variant === 'text' && styles.labelLight,
            ]}
          >
            {text}
          </Text>
          {subtitle && (
            <Text style={[styles.subtitle, { color: colors.text }]}>
              {subtitle}
            </Text>
          )}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    minHeight: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingVertical: 8,
  },
  destructiveBorder: {
    borderWidth: 1,
    borderColor: '#FCA5A5',
  },
  textButton: {
    height: 40,
  },
  textPressed: {
    opacity: 0.7,
  },
  disabled: {
    opacity: 0.5,
  },
  content: {
    alignItems: 'center',
  },
  label: {
    fontSize: 15,
    fontWeight: '500',
  },
  labelBold: {
    fontWeight: '600',
  },
  labelLight: {
    fontWeight: '400',
  },
  subtitle: {
    fontSize: 12,
    marginTop: 2,
    opacity: 0.8,
  },
});
